/**
 * Capacity diagrams: a boot loaded with cases, and a seating plan.
 *
 * Both are drawn from the class's own numbers rather than illustrated once
 * and reused, so a five-seat SUV and a three-seat saloon genuinely differ on
 * screen. They are the honest thing to show before the photography exists:
 * a diagram built from `max_persons` / `max_bags` states the real capacity,
 * where a grey "photo coming soon" panel states nothing at all.
 *
 * They do not replace the photographs. When a class has a gallery, the
 * gallery is shown instead — see ServiceClassTabs.
 */

/* Cases are split into check-in and carry-on only for drawing purposes: the
   API carries one `max_bags` total, so the split is presentational and the
   captions elsewhere never claim a breakdown the payload does not have. */
const splitBags = (bags: number) => {
  const check = Math.ceil(bags / 2);
  return { check, carry: Math.max(0, bags - check) };
};

function Case({ x, base, w, h }: { x: number; base: number; w: number; h: number }) {
  const ribs = [1, 2, 3].map((i) => (
    <path
      key={i}
      className="cap-art__rib"
      d={`M${(x + (w / 4) * i).toFixed(1)} ${base - h + 7}V${base - 7}`}
    />
  ));
  return (
    <g>
      <rect className="cap-art__shell" x={x} y={base - h} width={w} height={h} rx={6} />
      {ribs}
      <rect className="cap-art__band" x={x} y={base - h * 0.34} width={w} height={3} />
      <rect className="cap-art__handle" x={x + w / 2 - 7} y={base - h - 5} width={14} height={5} rx={2.5} />
    </g>
  );
}

export function LuggageArt({ bags }: { bags: number }) {
  const { check, carry } = splitBags(bags);
  const CHECK = { w: 46, h: 62 };
  const CARRY = { w: 34, h: 44 };
  const GAP = 9;
  const BASE = 120;

  const items = [
    ...Array.from({ length: check }, () => CHECK),
    ...Array.from({ length: carry }, () => CARRY),
  ];
  if (!items.length) return null;

  const natural =
    items.reduce((sum, item) => sum + item.w, 0) + GAP * (items.length - 1);
  /* Scale down so a van's worth of cases still fits the same frame. */
  const k = Math.min(1, 236 / natural);
  let x = (300 - natural * k) / 2;

  return (
    <svg viewBox="0 0 300 150" aria-hidden="true">
      <path className="cap-art__lid" d="M42 58C60 22 96 8 150 8s90 14 108 50" />
      <path
        className="cap-art__boot"
        d="M30 126V86c0-14 10-24 24-24h192c14 0 24 10 24 24v40z"
      />
      {items.map((item, index) => {
        const at = x;
        x += (item.w + GAP) * k;
        return (
          <Case key={index} x={at} base={BASE} w={item.w * k} h={item.h * k} />
        );
      })}
      <rect className="cap-art__boot" x={22} y={126} width={256} height={9} rx={4.5} />
    </svg>
  );
}

export function SeatingArt({ seats }: { seats: number }) {
  const rows = seats > 3 ? [92, 148, 200] : [92, 152];
  const TOP = 34;
  const BOTTOM = 80;
  const W = 32;
  const H = 32;

  const cells: JSX.Element[] = [];
  let placed = 0;

  rows.forEach((rx, rowIndex) => {
    [TOP, BOTTOM].forEach((ry) => {
      const isDriver = rowIndex === 0 && ry === TOP;
      if (!isDriver && placed >= seats) return;
      if (!isDriver) placed += 1;
      cells.push(
        <rect
          key={`${rx}-${ry}`}
          className={`cap-art__seat${isDriver ? " is-driver" : ""}`}
          x={rx}
          y={ry}
          width={W}
          height={H}
          rx={8}
        />
      );
      if (isDriver) {
        cells.push(
          <circle
            key="wheel"
            className="cap-art__wheel"
            cx={rx - 10}
            cy={ry + H / 2}
            r={7}
          />
        );
      }
    });
  });

  return (
    <svg viewBox="0 0 300 150" aria-hidden="true">
      <path
        className="cap-art__plan"
        d="M40 75c0-22 12-38 34-43l26-6h140c16 0 26 10 26 26v46c0 16-10 26-26 26H100l-26-6c-22-5-34-21-34-43z"
      />
      <path className="cap-art__wind" d="M76 40c-10 6-16 18-16 35s6 29 16 35z" />
      {cells}
    </svg>
  );
}
